(ns io.daskul.tutorial.webapp.views.conversation-thread
  "Custom thread view for conversations — replaces generic edit form.
   Shows message bubbles with compose bar (left) and conversation details sidebar (right).
   Polls for new messages every 5 seconds."
  (:require
   [uix.core :refer [defui $ use-effect use-state use-callback use-ref]]
   [uix.re-frame :as urf]
   [re-frame.core :as rf]
   [io.daskul.webapp.ui :as ui]
   [io.daskul.webapp.ui.icons :as icons]
   [io.daskul.webapp.aggregates.events :as agg.events]
   [io.daskul.webapp.aggregates.subs :as agg.subs]
   [io.daskul.webapp.hooks.aggregates :as hooks]
   [io.daskul.webapp.widgets.child-aggregates :as child-agg]
   [io.daskul.webapp.widgets.display :as display]
   [io.daskul.webapp.widgets.conversation.attachments :as att]
   [io.daskul.webapp.widgets.conversation.linked-leads :as ll]
   [io.daskul.webapp.widgets.fsm.transitions :as fsm.transitions]
   [io.daskul.webapp.widgets.layout.sidebar :as sidebar]
   [io.daskul.webapp.widgets.layout.slide-over :as slide-over]
   [io.daskul.webapp.widgets.form.lookup :as lookup]
   [io.daskul.webapp.widgets.form.lookup-create :as lookup-create]
   [io.daskul.tutorial.webapp.domain.active-context.subs :as ac.subs]
   [io.daskul.tutorial.webapp.domain.auth.subs :as auth.subs]
   [io.daskul.tutorial.webapp.domain.conversations.events :as conv.events]
   [io.daskul.tutorial.webapp.router :as router]
   [io.daskul.tutorial.webapp.views.conversation-thread.compose-state :as compose-state]
   [io.daskul.tutorial.webapp.views.conversation-thread.receipts :as receipts]))

;; ============================================================================
;; Chat Components
;; ============================================================================

(defui attachment-content
  "Renders the attachment portion of a message bubble by content-type.
   Any accompanying text is rendered separately by message-bubble."
  [{:keys [content-type attachments]}]
  (let [attachment (first attachments)
        url (:url attachment)]
    (case content-type
      "image"
      ;; Stickers and photos both arrive as content-type "image" (SMS-248) — a
      ;; sticker through the photo render is the giant-👍 bug. Cap it at
      ;; Messenger-like scale and drop the photo chrome; never upscale a small
      ;; sticker (max-* only, so the intrinsic size wins when it is smaller).
      (if (att/sticker? attachment)
        ($ :img {:src url
                 :alt "Sticker"
                 :data-testid "message-sticker"
                 :style {:max-width "128px" :max-height "128px" :display "block"}})
        ($ :img {:src url
                 :data-testid "message-photo"
                 :style {:max-width "100%" :border-radius "8px" :display "block"}}))

      "audio"
      ($ ui/group {:gap "xs" :wrap "nowrap"}
         ($ icons/icon-microphone {:size 16})
         ($ :audio {:controls true :src url :style {:height "32px" :max-width "220px"}}))

      "video"
      ($ ui/anchor {:href url :target "_blank" :rel "noopener noreferrer"}
         ($ ui/group {:gap "xs" :wrap "nowrap"}
            ($ icons/icon-video {:size 16})
            ($ ui/text {:size "sm"} "Video")))

      "file"
      ($ ui/anchor {:href url :target "_blank" :rel "noopener noreferrer"}
         ($ ui/group {:gap "xs" :wrap "nowrap"}
            ($ icons/icon-paperclip {:size 16})
            ($ ui/text {:size "sm"} (or (:filename attachment) "File"))))

      "fallback"
      ($ ui/anchor {:href url :target "_blank" :rel "noopener noreferrer"}
         ($ ui/group {:gap "xs" :wrap "nowrap"}
            ($ icons/icon-link {:size 16})
            ($ ui/text {:size "sm"} (get-in attachment [:provider-metadata :title] "Shared post"))))

      ($ ui/text {:size "sm" :c "dimmed" :fs "italic"} "This message type is not yet supported"))))

(defui message-bubble
  "Single message bubble — inbound left, outbound right.
   Failed outbound messages show a Retry button."
  [{:keys [message conversation-id]}]
  (let [{:keys [id direction text sent-at state content-type attachments reactions failure-reason edited-at]} message
        outbound? (= direction "outbound")
        failed? (and outbound? (= state "failed"))
        ;; #223 slice 2: Retry only where it can actually work. :needs-reconnect
        ;; is futile until an admin reconnects the Page, so offering Retry there
        ;; misleads; :generic (transient) and :outside-window (succeeds once the
        ;; customer replies — resend recomputes the window) keep it.
        retriable? (not= failure-reason "needs-reconnect")
        has-attachment? (and (seq attachments) (not= content-type "text"))
        resending? (urf/use-subscribe [::conv.events/resending? id])
        handle-resend (use-callback
                       (fn []
                         (rf/dispatch [::conv.events/resend-message conversation-id id]))
                       [conversation-id id])]
    ($ ui/box {:mb "xs"
               :style {:display "flex"
                       :justify-content (if outbound? "flex-end" "flex-start")}}
       ($ ui/paper
          {:shadow "xs"
           :p "sm"
           :radius "lg"
           :style {:max-width "80%"
                   :background-color (cond
                                       failed? "var(--mantine-color-red-0)"
                                       outbound? "var(--mantine-color-blue-0)"
                                       :else "var(--mantine-color-gray-0)")}}
          (when has-attachment?
            ($ attachment-content {:content-type content-type :attachments attachments}))
          (when text
            ($ ui/text {:size "sm" :mt (when has-attachment? 4)} text))
          ($ ui/group {:gap "xs" :mt 4 :justify (if outbound? "flex-end" "flex-start")}
             ($ ui/text {:size "xs" :c "dimmed"}
                (when sent-at
                  (.toLocaleTimeString (js/Date. sent-at) "en-US"
                                       #js {:hour "2-digit" :minute "2-digit"})))
             ;; SMS-246 (#226): a subtle "edited" marker when the customer edited
             ;; the message (presence of edited-at). Marker only for MVP — full
             ;; edit history is a deferred follow-up.
             (when edited-at
               ($ ui/text {:size "xs" :c "dimmed" :fs "italic"
                           :data-testid "message-edited-marker"}
                  "edited"))
             ;; #223 slice 3: sent → delivered → seen on outbound bubbles, from
             ;; the recipient receipts. receipts/outbound-status is nil for
             ;; inbound and for failed/pending (their own affordances).
             (when-let [status (receipts/outbound-status message)]
               ($ ui/text {:size "xs" :c "dimmed" :data-testid "message-receipt-status"}
                  (receipts/status-label status)))
             ;; SMS-245: what the customer reacted with. A rounded white pill
             ;; (Messenger-style); 18px emoji so it reads at a glance — Badge's
             ;; own font-size is too small even at "lg". ui/emoji renders in the
             ;; OS color-emoji font (the brand font would draw a black ❤️).
             (when (seq reactions)
               ($ ui/box {:data-testid "message-reactions"
                          :style {:display "inline-flex"
                                  :align-items "center"
                                  :gap "2px"
                                  :padding "2px 7px"
                                  :border-radius "999px"
                                  :background-color "var(--mantine-color-body)"
                                  :border "1px solid var(--mantine-color-gray-3)"
                                  :box-shadow "var(--mantine-shadow-xs)"
                                  :font-size "18px"
                                  :line-height 1}}
                  ($ ui/emoji (apply str (map :emoji reactions)))))
             (when failed?
               ;; #223 slice 2: a failed send says WHAT went wrong (from the
               ;; classified :failure-reason) so staff know whether to retry or
               ;; reconnect the Page. The red text carries the error signal;
               ;; Retry is neutral — it is a recovery action, not a destructive
               ;; one, so it should not read as red/danger.
               ($ ui/group {:gap 6 :wrap "nowrap" :align "center"}
                  ($ ui/text {:size "xs" :c "red" :fw 500
                              :data-testid "message-failure-reason"}
                     (case failure-reason
                       "needs-reconnect" "Reconnect the Facebook Page"
                       "outside-window" "Outside the reply window"
                       "Couldn't send"))
                  (when retriable?
                    ($ ui/button {:size "compact-xs"
                                  :variant "subtle"
                                  :color "gray"
                                  :loading resending?
                                  :onClick handle-resend
                                  :data-testid "message-retry"}
                       "Retry")))))))))

(defui message-thread
  "Scrollable list of message bubbles."
  [{:keys [messages loading? conversation-id]}]
  (let [scroll-ref (use-ref nil)]
    ;; Auto-scroll to bottom when messages change
    (use-effect
     (fn []
       (when-let [el @scroll-ref]
         (set! (.-scrollTop el) (.-scrollHeight el))))
     [messages])

    ($ ui/box {:ref scroll-ref
               :style {:flex 1
                       :overflow-y "auto"
                       :padding "16px"
                       :min-height 0}}
       (if (and loading? (empty? messages))
         ($ ui/center {:py "xl"} ($ ui/loader))
         (if (seq messages)
           (for [msg messages]
             ($ message-bubble {:key (:id msg)
                                :message msg
                                :conversation-id conversation-id}))
           ($ ui/center {:py "xl"}
              ($ ui/text {:c "dimmed"} "No messages yet")))))))

(defui compose-bar
  "Text input + send button at the bottom. Optional + icon for actions.
   `can-reply?` (default true) gates sending: when false, the textarea + Send are
   disabled (SMS-57 — school has no active Facebook Page, so a reply would 409).

   Mobile (#593): 44px touch targets, an icon-only Send (the word costs width
   that the textarea needs at 375px), and a 16px input font — Safari zooms the
   whole page whenever a focused input is under 16px, so every reply used to
   begin with an involuntary zoom. The accessible name stays \"Send\" at every
   width via aria-label, so role-based selectors keep working."
  [{:keys [on-send sending? on-create-lead can-reply?]
    :or {can-reply? true}}]
  (let [[text set-text!] (use-state "")
        disabled? (or (not can-reply?) sending?)
        handle-send (use-callback
                     (fn []
                       (when (and can-reply? (seq (.trim text)) (not sending?))
                         (on-send text)
                         (set-text! "")))
                     [text sending? on-send can-reply?])
        handle-keydown (use-callback
                        (fn [e]
                          (when (and (= (.-key e) "Enter") (not (.-shiftKey e)))
                            (.preventDefault e)
                            (handle-send)))
                        [handle-send])]
    ($ ui/group {:gap "sm"
                 :p "sm"
                 :align "flex-end"
                 :wrap "nowrap"
                 :style {:border-top "1px solid var(--mantine-color-gray-3)"
                         :background "var(--mantine-color-body)"
                         ;; Clear the iOS home indicator when the bar is the
                         ;; bottom-most thing on screen. This depends on
                         ;; `viewport-fit=cover` in index.html.template — without
                         ;; it env() resolves to 0px and Send lands inside the
                         ;; home-indicator area. Don't remove one without the
                         ;; other. Resolves to 0px on non-notched devices anyway.
                         :padding-bottom "calc(var(--mantine-spacing-sm) + env(safe-area-inset-bottom, 0px))"}}
       ;; Create-lead (and any future thread actions behind this +) is
       ;; independent of the reply window — capturing a lead from a conversation
       ;; you can't reply to (no Page, or outside the 7-day window) is still
       ;; valid. Only the textarea + Send below are gated by can-reply?.
       (when on-create-lead
         ($ ui/action-icon {:variant "light"
                            :size 44
                            :radius "xl"
                            :aria-label "Message actions"
                            :data-testid "composer-create-lead"
                            :onClick on-create-lead}
            ($ icons/icon-plus {:size 20})))
       ($ ui/textarea
          {:placeholder (if can-reply? "Type a message..." "Replies are unavailable")
           :value text
           :onChange #(set-text! (.. % -target -value))
           :onKeyDown handle-keydown
           :autosize true
           :minRows 1
           :maxRows 4
           :disabled disabled?
           :style {:flex 1 :min-width 0}
           ;; 16px kills Safari's focus-zoom; 44px min-height meets the touch
           ;; target. Both are set on the <textarea> itself, not the wrapper.
           :styles #js {:input #js {:fontSize "16px"
                                    :minHeight "44px"
                                    :paddingTop "10px"
                                    :paddingBottom "10px"}}})
       ($ ui/button
          {:onClick handle-send
           :loading sending?
           :disabled (or disabled? (empty? (.trim text)))
           :aria-label "Send"
           :data-testid "composer-send"
           :px "md"
           :h 44
           :leftSection ($ icons/icon-send {:size 18})}
          ;; The word is desktop-only; aria-label carries the name on mobile.
          ($ ui/box {:visibleFrom "sm" :component "span"} "Send")))))

(defui state-badge
  "A record's state as the accessible, human-labelled badge the Pipeline renders —
   \"Trial Scheduled\", not the raw \"trial-scheduled\" slug, on the #634 palette.
   `state-ui` is the FSM view-data every list item carries (ll/state-ui); with
   nil it degrades to the raw state on the neutral tone."
  [{:keys [state state-ui]}]
  (display/render-display-value
   state
   (display/state-ui->display-config state state-ui)))

(defui lead-chip
  "The thread header's lead indicator (#760) — whether this conversation is
   already a lead, and where that lead is at.

   It occupies the line the channel label used to: with Facebook Messenger the
   only integrated channel, that label named a distinction that doesn't exist,
   while the question staff actually have on this screen — \"have we captured
   this one, and what's next?\" — had no answer short of opening the aside.

   Tap targets follow from how much the chip can know: one lead → that lead;
   siblings → the leads list, since choosing between them isn't ours to guess;
   none → a menu, because \"no lead yet\" has two right answers (#767).

   `desktop?` rather than visibleFrom/hiddenFrom: the two branches differ in
   which element is interactive, and rendering both would put two chips in the
   DOM for every text assertion to trip over."
  [{:keys [chip suggestions desktop? on-open-details on-create-lead on-link-lead
           on-review]}]
  (let [{:keys [kind label subtitle lead-id state state-ui match-label]} chip
        testid "thread-lead-chip"

        ;; The match badge rides on every kind. Suggestions and this chip
        ;; classify differently ON PURPOSE — the chip counts any lead, the
        ;; server counts open ones — so a thread whose only lead is lost is
        ;; both `Lost` and unattached. Hanging the badge off :none alone left
        ;; that case with no route to its own suggestions, which on mobile
        ;; (where the aside panel is behind the drawer) meant no route at all.
        matches-badge (when match-label
                        ($ ui/badge {:size "sm" :variant "light" :radius "sm"
                                     :data-testid "thread-lead-chip-matches"}
                           match-label))

        match-items
        (when-let [rows (seq (:rows suggestions))]
          ($ :<>
             ($ ui/menu-label "Possible matches")
             (for [{:keys [kind id label kind-label] :as row} rows]
               ($ ui/menu-item
                  {:key (str (name kind) "-" id)
                   :data-testid (str "thread-lead-chip-suggestion-" (name kind))
                   :leftSection ($ (if (= kind :guardian)
                                     icons/icon-users
                                     icons/icon-user-circle)
                                   {:size 14})
                   :onClick #(on-review row)}
                  ;; kind-label UNDER the name, not beside it. This menu has no
                  ;; room for a state badge, so WHAT the row is ("Lead · New",
                  ;; "Guardian") is the thing it cannot leave out — and beside a
                  ;; name long enough to wrap it was exactly the part that got
                  ;; truncated to "L…". A plain div: Mantine's Stack margins
                  ;; fight MenuItem's padding, which made two rows overlap.
                  ($ :div {:style {:display "flex" :flexDirection "column"
                                   :gap 2 :minWidth 0 :paddingBlock 2}}
                     ($ ui/text {:size "sm" :lh 1.35} label)
                     ($ ui/text {:size "xs" :c "dimmed" :lh 1.2} kind-label))))
             (when (pos? (:more suggestions 0))
               ($ ui/menu-label
                  (str "+" (:more suggestions) " more — use Link an existing lead")))
             ($ ui/menu-divider)))

        ;; Wide enough for a real name. "Teresita Bautista – Rafael Bautista" is
        ;; 35 characters; at Mantine's default width it wrapped AND squeezed the
        ;; kind-label to "L…". 320 clears the common case on a 390px phone.
        ;; `$` is a UIx MACRO, so (apply $ …) resolves it as a value and blows
        ;; up at runtime with `cljs$lang$applyTo` — a blank page, not a compile
        ;; error. Extra items come in as one already-built fragment instead.
        in-menu (fn [target extra]
                  ($ ui/menu {:position "bottom-start" :withinPortal true :width 320}
                     ($ ui/menu-target target)
                     ($ ui/menu-dropdown match-items extra)))

        one-content ($ ui/group {:gap 6 :wrap "nowrap" :style {:min-width 0}}
                       ;; The Pipeline's own :lead icon (app/core icon-for).
                       ;; Without it a lone "NEW" in the header reads as the
                       ;; CONVERSATION's status — states like new/enrolled/lost
                       ;; carry no :card-subtitle to say otherwise.
                       ($ icons/icon-user-circle {:size 14})
                       ($ state-badge {:state state :state-ui state-ui})
                       ;; The next action is the point of the chip, but also the
                       ;; part that can run long, so it is what truncates — the
                       ;; badge never does.
                       (when subtitle
                         ($ ui/text {:size "xs" :c "dimmed" :truncate true} subtitle))
                       matches-badge)

        many-content ($ ui/group {:gap 4 :wrap "nowrap"}
                        ($ icons/icon-users {:size 14})
                        ($ ui/text {:size "xs" :c "dimmed"} label)
                        matches-badge)]
    (case kind
      :none
      ;; A menu rather than a straight jump to Create Lead. This chip is where
      ;; an unlinked thread becomes visible, so it is where the fix belongs —
      ;; and the two answers to "no lead yet" are genuinely different: a parent
      ;; new here needs a lead created, while one who phoned in yesterday
      ;; already HAS one, and creating a second is the duplicate #767 is about.
      ;; Matches come FIRST, above Create, for the same reason.
      (in-menu
       ($ ui/unstyled-button {:data-testid testid
                              :data-chip-kind "none"
                              :aria-label (if match-label
                                            (str "No lead yet — " match-label
                                                 ". Create or link one.")
                                            "No lead yet — create or link one")}
          ($ ui/group {:gap 6 :wrap "nowrap"}
             ($ icons/icon-plus {:size 14})
             ($ ui/text {:size "xs" :c "dimmed"} label)
             matches-badge))
       ($ :<>
          ($ ui/menu-item {:data-testid "thread-lead-chip-create"
                           :leftSection ($ icons/icon-plus {:size 14})
                           :onClick on-create-lead}
             "Create a new lead")
          ($ ui/menu-item {:data-testid "thread-lead-chip-link"
                           :leftSection ($ icons/icon-link {:size 14})
                           :onClick on-link-lead}
             "Link an existing lead")))

      :one
      (if match-label
        ;; With matches the chip has two jobs — report the lead AND offer the
        ;; others — so it becomes a menu and the lead moves into it. Without
        ;; them it stays a plain link, which is one tap instead of two.
        (in-menu
         ($ ui/unstyled-button {:data-testid testid
                                :data-chip-kind "one"
                                :aria-label (str label " — " (or (:label state-ui) state)
                                                 ", " match-label)}
            one-content)
         ($ ui/menu-item {:component "a"
                          :href (router/href :aggregate/detail
                                             {:agg "leads" :id lead-id})
                          :data-testid "thread-lead-chip-open"
                          :leftSection ($ icons/icon-user-circle {:size 14})}
            "Open this lead"))
        ($ ui/anchor {:href (router/href :aggregate/detail {:agg "leads" :id lead-id})
                      :underline "never"
                      :c "inherit"
                      :data-testid testid
                      :data-chip-kind "one"
                      :aria-label (str label " — " (or (:label state-ui) state))}
           one-content))

      :many
      (cond
        match-label
        (in-menu
         ($ ui/unstyled-button {:data-testid testid
                                :data-chip-kind "many"
                                :aria-label (str label " — " match-label)}
            many-content)
         ($ ui/menu-item {:data-testid "thread-lead-chip-open"
                          :leftSection ($ icons/icon-users {:size 14})
                          :onClick on-open-details}
            "See this thread's leads"))
        ;; On desktop the aside already lists them, so the chip is a count and
        ;; nothing more; on mobile that list is behind the Drawer this opens.
        desktop?
        ($ ui/box {:data-testid testid :data-chip-kind "many"} many-content)

        :else
        ($ ui/unstyled-button {:onClick on-open-details
                               :data-testid testid
                               :data-chip-kind "many"
                               :aria-label (str label " — open the list")}
           many-content))

      nil)))

(defui thread-header
  "Chat header — back control, sender name, lead chip, and (mobile only) the
   details button that opens the sidebar Drawer.

   The back control routes to the Inbox explicitly rather than calling
   window.history.back: a deep link into a thread (from a notification, or a
   pasted URL) has no in-app history to go back to, and history-back would leave
   the app entirely. It replaces the generic Back link the shell renders on other
   routes (see app/core.cljs main-content).

   The channel label this used to render — subtitle on mobile, badge on desktop —
   is gone (#760). It said \"facebook-messenger\" on every thread, because that is
   the only integrated channel, and it sat in the one line the lead chip needs.
   `:channel` stays on the record and still drives the slide-over's auto-fill,
   the list filter, and the inbox avatar mark, so re-adding the rendering when a
   second channel lands is a one-liner."
  [{:keys [conversation chip suggestions desktop? on-open-details on-create-lead
           on-link-lead on-review]}]
  (let [{:keys [sender-name]} conversation
        chip-el ($ lead-chip {:chip chip
                              :suggestions suggestions
                              :desktop? desktop?
                              :on-open-details on-open-details
                              :on-create-lead on-create-lead
                              :on-link-lead on-link-lead
                              :on-review on-review})]
    ($ ui/group {:justify "space-between"
                 :gap "xs"
                 :wrap "nowrap"
                 :px "xs"
                 :py "xs"
                 :data-testid "thread-header"
                 :style {:border-bottom "1px solid var(--mantine-color-gray-3)"
                         :background "var(--mantine-color-body)"}}
       ($ ui/group {:gap "xs" :wrap "nowrap" :style {:min-width 0}}
          ($ ui/action-icon {:component "a"
                             :href (router/href :inbox)
                             :variant "subtle"
                             :color "gray"
                             :size 44
                             :radius "xl"
                             :aria-label "Back to inbox"
                             :data-testid "thread-back"}
             ($ icons/icon-arrow-left {:size 20}))
          ($ ui/stack {:gap 0 :style {:min-width 0}}
             ;; `order 4` not 3: at 375px a long Filipino name at h3 wraps to two
             ;; lines and pushes the header taller than the messages it labels.
             ($ ui/title {:order 4 :lineClamp 1}
                (or sender-name "Unknown Sender"))
             ;; Mobile: under the name, where the channel subtitle was. There is
             ;; no room beside the title at 375px once the 44px back and details
             ;; targets have taken their share.
             (when-not desktop? chip-el)))
       ;; Desktop: beside the header actions, where the channel badge was.
       (when desktop? chip-el)
       ($ ui/action-icon {:variant "subtle"
                          :color "gray"
                          :size 44
                          :radius "xl"
                          :hiddenFrom "sm"
                          :aria-label "Conversation details"
                          :data-testid "thread-details"
                          :onClick on-open-details}
          ($ icons/icon-info-circle {:size 20})))))

;; ============================================================================
;; Linked Leads Sidebar Section
;; ============================================================================

(defui lead-row
  "One linked lead: the student name (what distinguishes siblings) over
   \"Program · Trial <when>\", with its state badge, tapping through to the lead.

   Shared by the aside list and the Create Lead slide-over's notice (#760) — the
   notice exists to be recognised as the same leads the aside shows, so the two
   must not drift into different shapes."
  [{:keys [lead school-tz]}]
  (let [lost? (ll/lost? lead)
        meta-line (ll/meta-line lead school-tz)]
    ($ ui/group {:justify "space-between" :gap "xs"
                 :align "flex-start" :wrap "nowrap"
                 ;; Active leads first, lost de-emphasized + last (SMS-35).
                 :style (when lost? {:opacity 0.55})}
       ($ ui/stack {:gap 0 :style {:min-width 0}}
          ($ ui/anchor {:href (router/href :aggregate/detail {:agg "leads" :id (:id lead)})
                        :size "sm" :fw 500 :lineClamp 1}
             (ll/primary-label lead))
          (when meta-line
            ($ ui/text {:size "xs" :c "dimmed"} meta-line)))
       ($ state-badge {:state (:state lead) :state-ui (ll/state-ui lead)}))))

(defui existing-lead-notice
  "\"This conversation already has a lead\" — shown in the Create Lead slide-over
   under the identity card (#760).

   A sign, not a gate: Create stays enabled and nothing asks for a confirmation.
   Siblings and family are a legitimate multi-lead path, and a confirm dialog on
   a legitimate path only trains people to click through it. Warning tone while
   something is still in play; a dimmed note once every lead here is closed,
   because a fresh message after a lost or enrolled lead usually is a new
   inquiry."
  [{:keys [notice school-tz]}]
  (let [{:keys [tone headline rows more]} notice
        warning? (= tone :warning)]
    ($ ui/alert {:variant "light"
                 :color (if warning? "yellow" "gray")
                 :p "sm"
                 :data-testid "existing-lead-notice"
                 :data-notice-tone (name tone)}
       ($ ui/stack {:gap "xs"}
          ;; Two literals rather than `:c (when-not warning? "dimmed")`: a nil
          ;; there reaches Mantine as JS null, and its colour parser rejects
          ;; anything non-string — "instead got object", because typeof null is.
          (if warning?
            ($ ui/text {:size "sm" :fw 500} headline)
            ($ ui/text {:size "sm" :fw 500 :c "dimmed"} headline))
          (for [lead rows]
            ($ lead-row {:key (:id lead) :lead lead :school-tz school-tz}))
          (when (pos? more)
            ($ ui/text {:size "xs" :c "dimmed"} (str "+" more " more")))))))

(defui create-lead-slide-over
  "Create Lead slide-over — progressive capture (SMS-106): mid live-chat the
   admin only needs the child's Age and a Program. The sender's identity (social
   handle + channel) is auto-filled from the conversation and shown as a
   read-only confirmation; everything else collapses under \"More details\".

   #593: mounted at PAGE level, not inside linked-leads-section. On mobile that
   section renders inside the details Drawer, and a closed Mantine Drawer does
   not mount its children — so a slide-over living in there could never open
   from the composer's + button (which also closes the Drawer on the way)."
  [{:keys [conversation-id conversation leads create-open? set-create-open!]}]
  (let [;; #760: the leads this conversation already has. Same sub as the header
        ;; chip and the aside — this is the last place the duplicate can still be
        ;; caught, and until now the slide-over said nothing about it.
        notice (ll/existing-notice leads)
        school-tz (urf/use-subscribe [::ac.subs/school-timezone])
        ;; SMS-271: default the (now visible, required) Branch selector. Prefer
        ;; the conversation's own branch; else the admin's active-context branch
        ;; (auto-pinned for single-branch schools — SMS-272). Nil for a multi-branch
        ;; admin on "All Branches" with an unassigned conversation, so the required
        ;; field prompts them to choose — which then claims the conversation to that
        ;; branch via the leads after-write propagate-branch-id! (T-015).
        active-branch-id (urf/use-subscribe [::ac.subs/branch-id])
        default-branch-id (or (:branch-id conversation) active-branch-id)
        initial-data (when conversation
                       (cond-> {:social-media-name (:sender-name conversation)
                                :comm-channel (case (:channel conversation)
                                                "facebook-messenger" "facebook-messenger"
                                                "other")
                                :lead-source (case (:channel conversation)
                                               "facebook-messenger" "facebook"
                                               nil)
                                :lead-source-platform-id (:channel-sender-id conversation)
                                :conversation-id conversation-id}
                         default-branch-id (assoc :branch-id default-branch-id)))]
    ($ slide-over/slide-over
       {:open? create-open?
        :on-close #(set-create-open! false)
        :title "Create Lead"}
       (when create-open?
         ($ :<>
            ;; Who-is-this confirmation (mirrors the auto-filled identity)
            ($ ui/box {:px "md" :pt "md"}
               ($ ui/paper {:withBorder true :p "sm" :radius "md"
                            :data-testid "lead-identity-confirmation"
                            :style {:background-color "var(--mantine-color-gray-0)"}}
                  ($ ui/group {:justify "space-between" :gap "xs" :wrap "nowrap"}
                     ($ ui/stack {:gap 2}
                        ($ ui/text {:size "xs" :c "dimmed"} "Creating lead for")
                        ($ ui/text {:size "sm" :fw 600}
                           (or (:sender-name conversation) "Unknown sender")))
                     (when (:channel conversation)
                       ($ ui/badge {:variant "light" :size "sm"}
                          (:channel conversation)))))
               ;; Kept on the slide-over even though the header's channel badge
               ;; is gone (#760): this one is the receipt for hidden auto-filled
               ;; values (lead-source, comm-channel), not decoration.
               (when notice
                 ($ ui/box {:mt "sm"}
                    ($ existing-lead-notice {:notice notice :school-tz school-tz}))))
            ($ lookup-create/lookup-inline-create
               {:resource "leads"
                :label "Lead"
                :initial-data initial-data
                ;; Identity (social-media-name, comm-channel) + guardian
                ;; (customer-id) are auto-filled and shown via the confirmation
                ;; above, so they stay hidden (values still submit), as do
                ;; conversation-id + platform-id. Branch (SMS-271) is NOT hidden:
                ;; it renders as a required selector defaulted from the
                ;; conversation / active-context, so a school-admin on "All
                ;; Branches" can pick one instead of hitting "Branch Id: Invalid
                ;; selection".
                :hidden-fields #{:conversation-id :lead-source-platform-id
                                 :social-media-name :comm-channel
                                 :customer-id}
                ;; Age, Grade Level and Program stay primary (#670, #757): what
                ;; front desk asks first is how old the child is and what grade
                ;; they're in, and those two answers are what decide which
                ;; program to suggest — so all three belong in front of the
                ;; admin, not one click away. The child's *name* is genuinely
                ;; optional at inquiry time and collapses with the rest under
                ;; "More details".
                ;;
                ;; This set only decides primary-vs-collapsed. The ORDER is the
                ;; server's — split-fields preserves the form schema's field
                ;; order, which is the leads aggregate's :create :input-fields
                ;; (NOT its :fields, as this comment claimed until #757). That
                ;; vector is what makes the form read Branch → Program → Age →
                ;; Grade Level.
                :advanced-fields #{:first-name :last-name :phone :email
                                   :student-first-name :student-last-name
                                   :lead-source}
                :form-field-component child-agg/default-form-field
                :on-created (fn [_new-id]
                              (set-create-open! false)
                              (rf/dispatch [::conv.events/fetch-linked-leads conversation-id]))
                :on-cancel #(set-create-open! false)}))))))

(defui link-lead-slide-over
  "\"Link an existing lead\" (#767) — attach a Lead that was created outside this
   thread (phone inquiry, walk-in, flyer, referral) to it.

   Deliberately a picker and nothing else. Everything the link implies —
   stamping the sender's PSID so this thread never needs matching again,
   claiming the conversation for the lead's branch, carrying the lead's guardian
   onto the thread — is the server's job, run by the same after-write hooks the
   automatic match uses. A form that asked the admin to confirm any of it would
   be asking them to re-state facts the lead already holds.

   The leads lookup is the search from the Pipeline: `search-fn` matches each
   term against the guardian name, the social handle and the student name, so
   \"Lim Grace\" finds \"Grace Lim\". The `:label-template` appends the student
   name, which is what tells sibling leads apart (SMS-178) — the one distinction
   a picker here genuinely has to make.

   Errors render inline rather than as a toast: the one that matters is 409
   lead-already-linked, and its message tells the admin to go unlink it first —
   which is a thing to read, not a thing to flash past.

   Open state is a subscription, not use-state: what dismisses this slide-over is
   a server round-trip, so ::link-lead-success is what closes it (the picker has
   no on-created callback to hang a local close on, the way Create Lead does)."
  [{:keys [conversation-id]}]
  (let [open? (urf/use-subscribe [::conv.events/link-lead-open?])
        [lead-id set-lead-id!] (use-state nil)
        linking? (urf/use-subscribe [::conv.events/linking-lead?])
        error (urf/use-subscribe [::conv.events/link-lead-error])
        close! (use-callback
                #(rf/dispatch [::conv.events/close-link-lead])
                [])]
    ;; Clear the selection whenever the slide-over opens. It is parent-level
    ;; state, so it survives the (when open? …) unmount below — without this, a
    ;; link that succeeded would leave the previous lead pre-selected the next
    ;; time the picker opens on a different thread.
    (use-effect
     (fn []
       (when open? (set-lead-id! nil))
       js/undefined)
     [open?])
    ($ slide-over/slide-over
       {:open? open?
        :on-close close!
        :title "Link an existing lead"}
       (when open?
         ($ ui/box {:p "md"}
            ($ ui/stack {:gap "md"}
               ($ ui/text {:size "sm" :c "dimmed"}
                  "Search by the parent's name, their social handle, or the child's name.")
               ($ lookup/lookup-field
                  {;; Its own re-frame slot, NOT :lead — the Pipeline's list
                   ;; state lives under that key, and a picker writing lookup
                   ;; options into it would fight the page behind the slide-over.
                   :agg-key :conversation-link-lead
                   :field {:name :lead-id
                           :schema "uuid"
                           :ui {:label "Lead"
                                :widget :autocomplete
                                :lookup-url "/leads/lookup"}}
                   :value lead-id
                   :on-change set-lead-id!})
               (when error
                 ($ ui/alert {:variant "light" :color "red"
                              :data-testid "link-lead-error"}
                    ($ ui/text {:size "sm"} error)))
               ($ ui/group {:justify "flex-end" :gap "sm"}
                  ($ ui/button {:variant "default"
                                :onClick close!
                                :data-testid "link-lead-cancel"}
                     "Cancel")
                  ($ ui/button {:disabled (or (nil? lead-id) linking?)
                                :loading linking?
                                :data-testid "link-lead-submit"
                                :onClick #(rf/dispatch [::conv.events/link-lead
                                                        conversation-id lead-id])}
                     "Link"))))))))

(defui suggested-links-panel
  "\"Is this someone you already know?\" (#767) — Leads and Guardians whose name
   matches the thread's sender, offered for the admin to CONFIRM.

   The point is WHERE this sits: slice 2 made linking possible, but only for an
   admin who thinks to look. The failure mode this issue is about is one who
   doesn't know a record already exists — they see \"No lead yet\", and the first
   thing on offer is Create a new lead. This panel puts the answer in front of
   that choice.

   Never auto-links. The leads search ANDs each term across three name columns,
   so Santos / Reyes / Cruz collide constantly — which is why each row carries
   what distinguishes one person from another (a lead's next action and state, a
   guardian's role), and why the admin presses the button.

   Renders nothing at all when the server offers nothing: the suppression rule
   (no guardian, no leads, not archived, usable sender-name) lives there, so
   this view has no opinion about when to appear."
  [{:keys [suggestions on-review linking?]}]
  (when-let [{:keys [rows more]} suggestions]
    ($ ui/paper {:p "md" :withBorder true :data-testid "suggested-links"}
       ($ ui/stack {:gap "sm"}
          ($ ui/text {:fw 500 :size "sm" :c "dimmed"}
             "Is this someone you already know?")
          (for [{:keys [kind id label kind-label subtitle] :as row} rows]
            ;; The name gets its OWN line, and may wrap. Name, state chip and
            ;; button abreast truncated the name to "Teresita Bautista – Rafael
            ;; Ba…" and the chip to "NE…" in a ~280px aside — the two things a
            ;; row exists to say. The chip is gone entirely: kind-label already
            ;; carries the state ("Lead · New"), so it was saying it twice and
            ;; losing both.
            ($ ui/stack {:key (str (name kind) "-" id) :gap 2
                         :data-testid (str "suggestion-" (name kind))}
               ($ ui/group {:justify "space-between" :align "flex-start"
                            :wrap "nowrap" :gap "xs"}
                  ($ ui/group {:gap 6 :wrap "nowrap" :align "flex-start"
                               :style {:flex 1 :minWidth 0}}
                     ($ ui/box {:style {:flexShrink 0 :paddingTop 2}}
                        ($ (if (= kind :guardian) icons/icon-users icons/icon-user-circle)
                           {:size 14}))
                     ($ ui/text {:size "sm" :fw 500 :style {:flex 1 :minWidth 0}} label))
                  ;; A text link, top-aligned with the NAME it acts on — not a
                  ;; pale pill parked beside the state line. `variant "light"`
                  ;; on a compact button renders as a rounded tinted capsule,
                  ;; which is the visual language of the state badges directly
                  ;; above it, so it read as one more label rather than the
                  ;; only thing on the row you can press. Matches "Link
                  ;; existing" in the Leads panel below, which is the same kind
                  ;; of secondary action.
                  ;;
                  ;; It opens a confirm rather than writing: these are guesses
                  ;; from a name search, and committing on one click asks the
                  ;; admin for a decision they have had no chance to check.
                  ($ ui/anchor {:component "button" :type "button"
                                :size "sm"
                                :style {:flexShrink 0}
                                :data-testid (str "suggestion-link-" (name kind))
                                :onClick #(when-not linking? (on-review row))}
                     "Review"))
               ;; Indented past the icon so it reads as belonging to the name.
               ($ ui/text {:size "xs" :c "dimmed" :truncate true
                           :style {:paddingLeft 20}}
                  (if subtitle (str kind-label " · " subtitle) kind-label))))
          ;; What the cap hid. Three rows shown silently would read as "these
          ;; are the candidates" rather than "these are three of them".
          (when (pos? more)
            ($ ui/text {:size "xs" :c "dimmed"}
               (str "+" more " more — use Link existing to search")))))))

(defui review-match-slide-over
  "Confirm a suggested match before writing it (#767).

   The suggestions are a trigram search over names — `Santos`, `Reyes` and
   `Cruz` collide constantly — so the panel and the chip menu FLAG, and this is
   where the admin decides. Linking straight off a row would ask them to commit
   to a guess they have had no chance to check, and a wrong merge costs far more
   than one extra tap.

   It also hides the record model, which is the part front desk should never
   have to hold: whichever kind of match this is, the admin answers one question
   — is this the same person? — and the server writes whatever that implies.
   Confirming a lead carries its guardian onto the thread too, via the
   after-write hook, so nobody has to know a chain exists."
  [{:keys [row conversation on-cancel on-confirm linking?]}]
  (let [{:keys [kind id label kind-label subtitle state state-ui facts]} row
        ;; A NEW TAB, not navigation. Checking one name should not cost the
        ;; thread and this half-made decision — `back` restores the page but not
        ;; the open sheet, so the admin would have to find the match again. It
        ;; is the escape hatch for the genuinely ambiguous case; the facts above
        ;; it are meant to settle most of them without leaving at all.
        href (when id
               (router/href :aggregate/detail
                            {:agg (if (= kind :guardian) "guardians" "leads")
                             :id id}))]
    ($ slide-over/slide-over
       {:open? (some? row)
        :on-close on-cancel
        :title "Is this the same person?"}
       (when row
         ($ ui/box {:p "md"}
            ($ ui/stack {:gap "md"}
               ($ ui/text {:size "sm" :c "dimmed"}
                  "Matched on the name this thread is from. Check it is the same "
                  "person before linking — the search only compares names.")
               ($ ui/paper {:withBorder true :p "sm" :radius "md"
                            :data-testid "review-match-card"}
                  ($ ui/stack {:gap "xs"}
                     ($ ui/group {:gap 6 :wrap "nowrap"}
                        ($ ui/text {:size "xs" :c "dimmed"} "This thread")
                        ($ ui/text {:size "sm" :fw 600}
                           (or (:sender-name conversation) "Unknown sender")))
                     ($ ui/divider)
                     ($ ui/group {:gap 6 :wrap "nowrap" :align "flex-start"}
                        ($ ui/box {:style {:flexShrink 0 :paddingTop 2}}
                           ($ (if (= kind :guardian) icons/icon-users icons/icon-user-circle)
                              {:size 14}))
                        ($ ui/text {:size "sm" :fw 600 :style {:flex 1 :minWidth 0}} label)
                        ;; flexShrink 0, or a two-line name squeezes the state
                        ;; chip to "NE…" — the one part of the row that must
                        ;; stay legible is what was being truncated.
                        (when state
                          ($ ui/box {:style {:flexShrink 0}}
                             ($ state-badge {:state state :state-ui state-ui}))))
                     ($ ui/text {:size "xs" :c "dimmed"}
                        (if subtitle (str kind-label " · " subtitle) kind-label))
                     ;; The facts a name cannot supply. The search matched on
                     ;; the NAME, so the name is the one thing that cannot tell
                     ;; two people apart — the child, the phone and how they
                     ;; first reached us are what actually decide it.
                     (when (seq facts)
                       ($ ui/stack {:gap 2 :mt 4}
                          (for [{:keys [label value]} facts]
                            ($ ui/group {:key label :gap 6 :wrap "nowrap"
                                         :align "flex-start"}
                               ($ ui/text {:size "xs" :c "dimmed"
                                           :style {:width 56 :flexShrink 0}}
                                  label)
                               ($ ui/text {:size "xs"} value)))))
                     ($ ui/anchor {:href href :target "_blank" :rel "noopener noreferrer"
                                   :size "xs"
                                   :data-testid "review-match-open"}
                        (if (= kind :guardian)
                          "Open guardian in a new tab"
                          "Open lead in a new tab"))))
               ($ ui/group {:justify "flex-end" :gap "sm"}
                  ($ ui/button {:variant "default" :onClick on-cancel
                                :data-testid "review-match-cancel"}
                     "Not them")
                  ($ ui/button {:loading linking?
                                :data-testid "review-match-confirm"
                                :onClick #(on-confirm row)}
                     "Yes, link"))))))))

(defui linked-leads-section
  "Shows leads linked to this conversation. Both slide-overs (Create Lead and
   Link an existing lead) are siblings at page level, not children of this.

   `leads` is a prop, not a subscription: the fetch and the sub both live at
   page level (#760). Below `sm` this section is unmounted whenever the details
   Drawer is closed, so a fetch owned here never ran on the surface that needs
   it most — the header chip and the slide-over notice read the same data."
  [{:keys [leads loading? on-link-lead]}]
  (let [;; School tz so a trial start renders on the same wall-clock as the
        ;; calendar (SMS-49), regardless of the admin's browser tz.
        school-tz (urf/use-subscribe [::ac.subs/school-timezone])]
    ($ :<>
       ($ ui/paper {:p "md" :withBorder true :data-testid "linked-leads"}
          ($ ui/stack {:gap "sm"}
             ($ ui/group {:justify "space-between" :gap "xs" :wrap "nowrap"}
                ($ ui/text {:fw 500 :size "sm" :c "dimmed"} "Leads")
                ;; Here as well as on the header chip, because the chip's menu
                ;; only exists in the no-lead case. A thread that already has one
                ;; lead can still be missing a sibling's, and this panel is where
                ;; that relationship is read — so it is where adding to it goes.
                (when on-link-lead
                  ($ ui/anchor {:component "button" :type "button"
                                :size "xs"
                                :data-testid "linked-leads-link-existing"
                                :onClick on-link-lead}
                     "Link existing")))

             ;; nil leads = not loaded for THIS conversation yet; [] = loaded and
             ;; empty. The loader needs `loading?` too, or a failed fetch would
             ;; spin forever instead of falling through to "No leads yet".
             (if (and loading? (nil? leads))
               ($ ui/center {:py "xs"} ($ ui/loader {:size "sm"}))
               (if (seq leads)
                 ;; Active leads first, lost de-emphasized + last (SMS-35).
                 (for [lead (ll/order-rows leads)]
                   ($ lead-row {:key (:id lead) :lead lead :school-tz school-tz}))
                 ($ ui/text {:size "sm" :c "dimmed"} "No leads yet"))))))))

;; ============================================================================
;; Main Page
;; ============================================================================

(def ^:private poll-interval-ms 5000)

(defui no-page-notice
  "Shown above the disabled compose bar when the school has no active Facebook
   Page (SMS-57). Role-aware (SMS-56 — only school-admins can connect a Page):
   - school-admin → 'Connect a Facebook Page' link to /settings
   - branch-admin → 'Ask your school admin to connect a Facebook Page' (no link)"
  [{:keys [role]}]
  (let [school-admin? (= role "school-admin")]
    ($ ui/alert {:variant "light"
                 :color "yellow"
                 :data-testid "no-facebook-page-notice"
                 :style {:border-radius 0
                         :border-top "1px solid var(--mantine-color-gray-3)"}}
       (if school-admin?
         ($ ui/text {:size "sm"}
            "Replies are unavailable — no Facebook Page is connected. "
            ($ ui/anchor {:href (router/href :settings)
                          :data-testid "connect-facebook-page-link"}
               "Connect a Facebook Page"))
         ($ ui/text {:size "sm"}
            "Replies are unavailable — ask your school admin to connect a Facebook Page.")))))

(defui outside-reply-window-notice
  "Shown above the disabled compose bar when the Facebook Page is connected but
   Meta's reply window has closed (#223). Distinct from the no-Page notice: the
   Page is fine — the customer simply hasn't messaged within 7 days, so no
   standard send is possible and the composer blocks rather than letting staff
   type a message the API will refuse. Not role-gated: no admin action reopens
   it; only a new inbound from the customer does."
  []
  ($ ui/alert {:variant "light"
               :color "gray"
               :data-testid "outside-reply-window-notice"
               :style {:border-radius 0
                       :border-top "1px solid var(--mantine-color-gray-3)"}}
     ($ ui/text {:size "sm"}
        "Replies are unavailable — this conversation is outside Facebook's reply "
        "window. Messenger only allows replies within 7 days of the customer's "
        "last message.")))

(defui conversation-thread-page []
  (let [route (urf/use-subscribe [:io.daskul.tutorial.webapp.router/current-route])
        conversation-id (get-in route [:path-params :id])
        ;; Conversation data via aggregate sub
        conversation (urf/use-subscribe [::agg.subs/by-id :conversation conversation-id])
        conv-loading? (urf/use-subscribe [::agg.subs/loading? :conversation])
        ;; Messages via aggregate subs
        messages (urf/use-subscribe [::agg.subs/all :message])
        msgs-loading? (urf/use-subscribe [::agg.subs/loading? :message])
        sending? (urf/use-subscribe [::conv.events/sending?])
        ;; Current user's role — drives the role-aware no-Page notice (SMS-57).
        role (urf/use-subscribe [::auth.subs/role])
        ;; FSM hook for state + transitions
        ;; state/state-ui come off the conversation record itself (rendered by
        ;; the actions panel below); the hook is here for the transitions.
        {:keys [transitions transition! transition-event]}
        (hooks/use-fsm {:aggregate :conversation :id conversation-id})
        ;; Leads linked to this conversation (#760). Fetched and subscribed HERE,
        ;; not in linked-leads-section: below `sm` that section only exists while
        ;; the details Drawer is open, so a fetch owned by it never ran on the
        ;; surface that needs it most. One sub feeds the header chip, the aside
        ;; list, and the Create Lead slide-over's existing-lead notice.
        leads (urf/use-subscribe [::conv.events/linked-leads conversation-id])
        leads-loading? (urf/use-subscribe [::conv.events/leads-loading?])
        ;; #767: who this thread might already be, when it is attached to
        ;; nobody. The server decides whether there is anything to offer.
        suggested (urf/use-subscribe [::conv.events/suggested-links conversation-id])
        school-tz (urf/use-subscribe [::ac.subs/school-timezone])
        linking-lead? (urf/use-subscribe [::conv.events/linking-lead?])
        ;; One view-model, two surfaces: the aside panel on desktop and the
        ;; header chip's menu — which on a phone is the only one reachable,
        ;; since the aside lives behind the details drawer there.
        suggestion-vm (ll/suggestions suggested school-tz)
        ;; Composer reply-gating (SMS-57 + #223), from backend after-read flags
        ;; refreshed by the 5s poll: :facebook-page-connected? and :reply-window.
        ;; A reply blocks when the Page is inactive (would 409) or Meta's 7-day
        ;; window has closed — with a distinct notice each, and no-Page winning.
        ;; While the conversation is still loading the composer stays enabled to
        ;; avoid a load-time notice flash. See compose-state (pure, unit-tested).
        {:keys [can-reply? notice]} (compose-state/compose-state conversation)
        ;; Create Lead slide-over state (shared by sidebar + compose bar)
        [create-open? set-create-open!] (use-state false)
        ;; #593: below `sm` the aside's content lives in a Drawer instead of a
        ;; column. This is the case the mobile-first guide's visibleFrom/hiddenFrom
        ;; rule can't cover — rendering both branches would mount
        ;; linked-leads-section twice and fire its fetch twice — so the breakpoint
        ;; has to pick which one exists.
        desktop? (ui/use-media-query (:sm ui/breakpoints))
        [details-open? set-details-open!] (use-state false)
        handle-send
        (use-callback
         (fn [text]
           (rf/dispatch [::conv.events/send-reply conversation-id text]))
         [conversation-id])
        ;; The match the admin is reviewing, or nil. Both the aside panel and
        ;; the chip menu set it; neither writes anything itself.
        [review-row set-review-row!] (use-state nil)
        handle-confirm-match
        (use-callback
         (fn [{:keys [kind id]}]
           ;; One decision, both writes. A guardian goes on the thread via the
           ;; FSM `link` transition that already ships; a lead goes through
           ;; link-lead, whose after-write carries ITS guardian onto the thread
           ;; too — so the admin never has to know which of the two they picked
           ;; implies the other.
           (if (= kind :guardian)
             (transition! "link" {:customer-id id})
             (rf/dispatch [::conv.events/link-lead conversation-id id]))
           (set-review-row! nil))
         [conversation-id transition!])
        handle-link-lead
        (use-callback
         (fn []
           ;; Same Drawer-vs-slide-over rule as Create Lead below — and it bites
           ;; harder here, since "Link existing" lives INSIDE the Drawer's leads
           ;; panel on mobile.
           (set-details-open! false)
           (rf/dispatch [::conv.events/open-link-lead]))
         [])
        handle-create-lead
        (use-callback
         (fn []
           ;; Opening Create Lead from inside the details Drawer would stack two
           ;; overlays; close the Drawer as the slide-over takes over.
           (set-details-open! false)
           (set-create-open! true))
         [])]

    ;; Fetch conversation + messages on open, then poll both every 5s.
    ;; Messages are polled directly rather than gated on last-message-at:
    ;; reactions (SMS-245) — and future edits/unsends (SMS-246/247) — mutate a
    ;; message row in place WITHOUT bumping the conversation's last-message-at,
    ;; so a last-message-at gate would surface them only on a manual refresh.
    ;; A poll that returns identical data is = to current state, so re-frame
    ;; skips the re-render (no flicker, no scroll jump) — only real changes cost.
    (use-effect
     (fn []
       (when conversation-id
         (let [refresh! (fn []
                          (rf/dispatch [::agg.events/get :conversation conversation-id])
                          (rf/dispatch [::agg.events/list :message
                                        (assoc conv.events/msg-params :conversation-id conversation-id)]))]
           (refresh!)
           (let [timer (js/setInterval refresh! poll-interval-ms)]
             (fn [] (js/clearInterval timer))))))
     [conversation-id])

    ;; Mark conversation as read on thread open (SMS-29) — one-shot, not polled
    (use-effect
     (fn []
       (when conversation-id
         (rf/dispatch [::conv.events/mark-as-read conversation-id])))
     [conversation-id])

    ;; Linked leads, on open and whenever the thread changes (#760). One-shot,
    ;; not polled: leads change when THIS admin creates one, and that path
    ;; re-dispatches on success.
    (use-effect
     (fn []
       (when conversation-id
         (rf/dispatch [::conv.events/fetch-linked-leads conversation-id])
         (rf/dispatch [::conv.events/fetch-suggested-links conversation-id]))
       js/undefined)
     [conversation-id])

    ;; The details panel — status/transitions + linked leads. Rendered in exactly
    ;; one place: the aside column on desktop, the Drawer on mobile.
    (let [details
          ($ ui/stack {:gap "md"}
             ;; Link to Guardian / Archive. These went missing when #696 moved
             ;; transitions out of the sidebar and into the record header: that
             ;; commit reshaped edit-sidebar to render note-alerts ONLY, and this
             ;; custom view — which has its own chat header and never renders the
             ;; generic record header — kept handing it :transitions it now drops
             ;; on the floor. So the conversation FSM's only two admin actions
             ;; had no UI at all. transition-action-panel is the piece #696
             ;; extracted for exactly this rail, and it takes the props this page
             ;; was already computing.
             ;; Gated on the CONVERSATION, not on (seq transitions): `linked`
             ;; and `archived` declare no transitions, so gating the card on
             ;; them meant a thread showed its state right up until the moment
             ;; it had one worth showing, then went blank. The panel inside is
             ;; what's conditional.
             (when conversation
               ($ ui/paper {:p "md" :withBorder true
                            :data-testid "conversation-actions"}
                  ($ ui/group {:justify "space-between" :align "center" :mb "sm"}
                     ($ ui/text {:fw 500 :size "sm" :c "dimmed"} "Conversation")
                     ;; The thread's own state (new / linked / archived). It went
                     ;; with the transitions in #696 — the record header shows it
                     ;; on generic pages, and this view has no record header.
                     ($ state-badge {:state (:state conversation)
                                     :state-ui (get-in conversation [:_meta :fsm :state])}))
                  (when (seq transitions)
                    ($ fsm.transitions/transition-action-panel
                       {:transitions transitions
                        :on-transition transition!
                        :loading-event transition-event
                        :agg-key :conversation
                        :record conversation}))))
             ;; Note-severity alerts, which is all this renders since #701.
             (when conversation
               ($ sidebar/edit-sidebar
                  {:notes (get-in conversation [:_meta :alerts])}))
             ($ suggested-links-panel
                {:suggestions suggestion-vm
                 :linking? linking-lead?
                 :on-review set-review-row!})
             ($ linked-leads-section {:leads leads
                                      :loading? leads-loading?
                                      :on-link-lead handle-link-lead}))]
      ($ ui/box {:className "conversation-thread"}
        ;; Chat: header + messages + composer. A column flexbox whose only
        ;; scrolling child is the message list, so the header and composer stay
        ;; put while the messages move.
         ($ ui/card {:className "conversation-thread__chat"
                     :shadow "sm" :padding 0 :radius "md" :withBorder true
                     :style {:display "flex"
                             :flex-direction "column"
                             ;; min-height:0 is what lets the message list
                             ;; actually scroll instead of growing the card.
                             :min-height 0}}
            (when conversation
              ($ thread-header {:conversation conversation
                                :chip (ll/header-chip leads suggestion-vm)
                                :suggestions suggestion-vm
                                :desktop? desktop?
                                :on-open-details #(set-details-open! true)
                                :on-create-lead handle-create-lead
                                :on-link-lead handle-link-lead
                                :on-review set-review-row!}))

         ;; Loading state for conversation
            (when (and conv-loading? (not conversation))
              ($ ui/center {:py "xl"} ($ ui/loader)))

         ;; Message thread
            ($ message-thread {:messages messages
                               :loading? msgs-loading?
                               :conversation-id conversation-id})

         ;; Reply-gating notice above the disabled bar (SMS-57 no-Page, role-aware;
         ;; #223 outside-reply-window). compose-state picks at most one.
            (case notice
              :no-page ($ no-page-notice {:role role})
              :window-closed ($ outside-reply-window-notice)
              nil)

         ;; Compose bar
            ($ compose-bar {:on-send handle-send
                            :sending? sending?
                            :on-create-lead handle-create-lead
                            :can-reply? can-reply?}))

      ;; Desktop: aside column. Mobile: the same content behind the header's
      ;; details button.
         (if desktop?
           ($ ui/box {:className "conversation-thread__aside"} details)
           ;; Full-screen on a phone: a partial-height sheet leaves the thread
           ;; peeking behind it and gives the leads list less room than it needs.
           ($ ui/drawer {:opened details-open?
                         :onClose #(set-details-open! false)
                         :position "bottom"
                         :size "100%"
                         :title "Conversation details"
                         :data-testid "thread-details-drawer"}
              ($ ui/box {:pb "md"} details)))

      ;; Page-level, deliberately outside the aside/Drawer branch above: on
      ;; mobile the aside's content is unmounted while the Drawer is closed, and
      ;; the composer's + closes that Drawer on its way to opening this — so a
      ;; slide-over nested in there could never appear (#593).
         ($ create-lead-slide-over
            {:conversation-id conversation-id
             :conversation conversation
             :leads leads
             :create-open? create-open?
             :set-create-open! set-create-open!})

      ;; Page-level for the same reason (#593): "Link existing" is reachable
      ;; from the leads panel, which on mobile lives inside the Drawer this
      ;; closes on its way here.
         ($ link-lead-slide-over {:conversation-id conversation-id})

      ;; Page-level for the same #593 reason as the others: the chip menu that
      ;; opens this closes the details drawer on its way.
         ($ review-match-slide-over
            {:row review-row
             :conversation conversation
             :linking? linking-lead?
             :on-cancel #(set-review-row! nil)
             :on-confirm handle-confirm-match})))))
